import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import {
  email_accounts_path,
  email_account_path,
  capacities_email_accounts_path,
  statuses_email_accounts_path,
} from "~/lib/routes"
import { useEmailAccountUsage } from "~/react/hooks/useEmailAccountUsage"
import { useActionCable } from "~/react/hooks/useActionCable"
import type { EmailAccount, EmailAccountsData } from "~/react/components/email_accounts/email_accounts_filters"
import type { CapacityData } from "../utils"

const STATUS_FETCH_TIMEOUT_MS = 15000

type UseEmailAccountsDataReturn = {
  data: EmailAccountsData | null
  loading: boolean
  error: string | null
  capacityData: Record<number, CapacityData>
  statusData: Record<string, { status: string }>
  statusFetchError: boolean
  loadingCapacity: Set<number>
  fetchData: (showLoading?: boolean) => Promise<EmailAccountsData | null>
  fetchStatuses: () => Promise<void>
  setData: React.Dispatch<React.SetStateAction<EmailAccountsData | null>>
  setCapacityData: React.Dispatch<React.SetStateAction<Record<number, CapacityData>>>
  setActiveAccount: React.Dispatch<React.SetStateAction<EmailAccount | null>>
  activeAccount: EmailAccount | null
  activeAccountRef: React.RefObject<EmailAccount | null>
}

export function useEmailAccountsData(onEmailAccountIdChange?: (id: number | null) => void): UseEmailAccountsDataReturn {
  const [data, setData] = useState<EmailAccountsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [capacityData, setCapacityData] = useState<Record<number, CapacityData>>({})
  const [statusData, setStatusData] = useState<Record<string, { status: string }>>({})
  const [statusFetchError, setStatusFetchError] = useState(false)
  const [loadingCapacity, setLoadingCapacity] = useState<Set<number>>(new Set())
  const [activeAccount, setActiveAccount] = useState<EmailAccount | null>(null)

  const activeAccountRef = useRef<EmailAccount | null>(null)

  // Keep ref in sync for use in async callbacks
  useEffect(() => {
    activeAccountRef.current = activeAccount
  }, [activeAccount])

  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      const response = await fetch(email_accounts_path({ format: "json" }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (!response.ok) {
        throw new Error(i18n.t("js.email_accounts_center.errors.http_error", { status: response.status }))
      }

      const json = await response.json()
      setData(json)
      setStatusData({}) // Clear so refetched list shows loading until fetchStatuses() completes
      setError(null)
      return json
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("js.email_accounts_center.errors.load_failed"))
      return null
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Fetch capacity data for all accounts in a single request
  const fetchCapacities = useCallback(async () => {
    try {
      const response = await fetch(capacities_email_accounts_path({ format: "json" }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (response.ok) {
        const capacities = await response.json()
        setCapacityData(capacities)
      }
    } catch (err) {
      console.error("Failed to fetch capacity data:", err)
    }
  }, [])

  const getFallbackStatusData = useCallback((accountsData: EmailAccountsData | null) => {
    if (!accountsData) return {}
    const accounts = [...(accountsData.connected ?? []), ...(accountsData.managed ?? [])]
    return Object.fromEntries(
      accounts
        .filter((a): a is EmailAccount & { id: number } => a.id != null)
        .map((a) => [String(a.id), { status: "disconnected" as const }]),
    )
  }, [])

  const fetchStatuses = useCallback(async () => {
    setStatusFetchError(false)
    const fallback = getFallbackStatusData(data ?? null)
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const applyFallback = () => {
      setStatusData((prev) => (Object.keys(prev).length > 0 ? prev : fallback))
      setStatusFetchError(true)
    }

    timeoutId = setTimeout(applyFallback, STATUS_FETCH_TIMEOUT_MS)

    try {
      const response = await fetch(statuses_email_accounts_path({ format: "json" }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (response.ok) {
        if (timeoutId != null) clearTimeout(timeoutId)
        timeoutId = null
        const statuses = await response.json()
        setStatusData(statuses)
        setStatusFetchError(false)
      } else {
        applyFallback()
      }
    } catch (err) {
      console.error("Failed to fetch status data:", err)
      applyFallback()
    } finally {
      if (timeoutId != null) clearTimeout(timeoutId)
    }
  }, [data, getFallbackStatusData])

  // Fetch capacity data for a single account (used for real-time updates)
  const fetchCapacityData = useCallback(async (accountId: number) => {
    try {
      setLoadingCapacity((prev) => new Set(prev).add(accountId))
      const response = await fetch(email_account_path(accountId, { format: "json" }), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (response.ok) {
        const json = await response.json()
        const capacity: CapacityData = {
          sent: json.sent_in_current_daily_limit ?? 0,
          limit: json.limit ?? 0,
          scheduled: json.scheduled ?? 0,
        }
        setCapacityData((prev) => ({ ...prev, [accountId]: capacity }))

        // Also update display_name in accounts state if it changed
        if (json.display_name !== undefined) {
          setData((prev) => {
            if (!prev) return prev
            const updateAccount = (account: EmailAccount) =>
              account.id === accountId ? { ...account, display_name: json.display_name } : account
            return {
              ...prev,
              connected: prev.connected?.map(updateAccount),
              managed: prev.managed?.map(updateAccount),
            }
          })
        }
      }
    } catch (err) {
      console.error(`Failed to fetch capacity data for account ${accountId}:`, err)
    } finally {
      setLoadingCapacity((prev) => {
        const next = new Set(prev)
        next.delete(accountId)
        return next
      })
    }
  }, [])

  const handleUsageUpdate = useCallback(
    (accountId: number) => {
      fetchCapacityData(accountId)
    },
    [fetchCapacityData],
  )

  useEmailAccountUsage({ onUpdate: handleUsageUpdate })

  // Fetch capacity and status data when accounts data is loaded
  useEffect(() => {
    if (!data) return
    fetchCapacities()
    fetchStatuses()
  }, [data, fetchCapacities, fetchStatuses])

  // Subscribe for push updates to account health
  const healthCheckChannelParams = useMemo(() => ({ channel: "HealthChecksChannel" }), [])

  const handleHealthCheckReceived = useCallback(
    (wsData: { domain: string; score: number | null; report_status?: string | null }) => {
      const domain = wsData.domain?.toLowerCase()
      if (!domain) return

      const healthCheckStatus = wsData.report_status as "pending" | "completed" | null

      const healthValue = wsData.score ?? null

      const updateAccounts = (accounts: EmailAccount[] | undefined) =>
        accounts?.map((acc) => {
          const accDomain = acc.email.split("@")[1]?.toLowerCase()
          if (accDomain === domain) {
            // Don't revert from completed (null) back to pending
            // This can happen if ActionCable messages arrive out of order
            if (acc.health_check_status === null && healthCheckStatus === "pending") {
              return acc
            }
            return {
              ...acc,
              health: healthValue,
              health_check_status: healthCheckStatus,
            }
          }
          return acc
        })

      setData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          connected: updateAccounts(prev.connected) ?? prev.connected,
          managed: updateAccounts(prev.managed) ?? prev.managed,
        }
      })

      setActiveAccount((prev) => {
        if (!prev) return prev
        const accDomain = prev.email.split("@")[1]?.toLowerCase()
        if (accDomain === domain) {
          // Don't revert from completed (null) back to pending
          if (prev.health_check_status === null && healthCheckStatus === "pending") {
            return prev
          }
          return {
            ...prev,
            health: healthValue,
            health_check_status: healthCheckStatus,
          }
        }
        return prev
      })
    },
    [],
  )

  const healthCheckCallbacks = useMemo(() => ({ received: handleHealthCheckReceived }), [handleHealthCheckReceived])

  useActionCable({
    params: healthCheckChannelParams,
    callbacks: healthCheckCallbacks,
  })

  // Subscribe to email accounts channel for disconnections and updates
  const emailAccountsChannelParams = useMemo(() => ({ channel: "EmailAccountsChannel" }), [])

  const handleEmailAccountsReceived = useCallback(
    (wsData: { type: string; count?: number }) => {
      if (wsData.type === "accounts_disconnected" || wsData.type === "subscription_canceled") {
        fetchData(false).then((freshData) => {
          const current = activeAccountRef.current
          if (!current) return

          const allAccounts = [...(freshData?.connected || []), ...(freshData?.managed || [])]
          const accountStillExists = allAccounts.some((acc) => acc.id === current.id)
          if (!accountStillExists) {
            setActiveAccount(null)
            onEmailAccountIdChange?.(null)
          }
        })
      }
    },
    [fetchData, onEmailAccountIdChange],
  )

  const emailAccountsCallbacks = useMemo(
    () => ({ received: handleEmailAccountsReceived }),
    [handleEmailAccountsReceived],
  )

  useActionCable({
    params: emailAccountsChannelParams,
    callbacks: emailAccountsCallbacks,
  })

  return {
    data,
    loading,
    error,
    capacityData,
    statusData,
    statusFetchError,
    loadingCapacity,
    fetchData,
    fetchStatuses,
    setData,
    setCapacityData,
    setActiveAccount,
    activeAccount,
    activeAccountRef,
  }
}
